import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ThumbsUp, CalendarDays, MessageSquare, Star, CheckCircle } from 'lucide-react';
import api from '@/lib/api';
import { formatDateTimeShort } from '@/lib/datetime';
import { cn } from '@/lib/utils';

const FACES = [
    { rating: 1, label: 'Muy insatisfecho', emoji: '😠' },
    { rating: 2, label: 'Insatisfecho', emoji: '🙁' },
    { rating: 3, label: 'Neutral', emoji: '😐' },
    { rating: 4, label: 'Satisfecho', emoji: '🙂' },
    { rating: 5, label: 'Muy satisfecho', emoji: '😍' },
];

export default function CustomerSatisfaction() {
    const [attendances, setAttendances] = useState([]);
    const [ratings, setRatings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('pending');

    const [selectedAttendance, setSelectedAttendance] = useState(null);
    const [selectedRating, setSelectedRating] = useState(null);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        const fetch = async () => {
            try {
                const [rateableRes, ratingsRes] = await Promise.all([
                    api.get('/customer/rateable-classes'),
                    api.get('/customer/ratings'),
                ]);
                setAttendances(rateableRes.data.data || []);
                setRatings(ratingsRes.data.data || []);
            } catch (err) {
                setError(err.response?.data?.message || 'Error al cargar los datos.');
            } finally { setLoading(false); }
        };
        fetch();
    }, []);

    const handleSubmit = async () => {
        if (!selectedAttendance || !selectedRating) return;
        setSubmitting(true);
        try {
            await api.post('/customer/ratings', {
                class_attendance_id: selectedAttendance.attendance_id,
                rating: selectedRating,
                comment: comment.trim() || undefined,
            });
            setSubmitted(true);
            setTimeout(() => {
                setAttendances(prev => prev.filter(a => a.attendance_id !== selectedAttendance.attendance_id));
                setSelectedAttendance(null);
                setSelectedRating(null);
                setComment('');
                setSubmitted(false);
                // Refresh ratings list
                api.get('/customer/ratings').then(r => setRatings(r.data.data || []));
            }, 2000);
        } catch (err) {
            setError(err.response?.data?.message || 'Error al enviar la calificación.');
        } finally { setSubmitting(false); }
    };

    if (loading) return <p className="text-white/60 text-center py-8">Cargando...</p>;

    return (
        <div>
            <h1 className="text-2xl font-bold uppercase tracking-wide mb-6">Satisfacción</h1>

            {error && (
                <div className="mb-4 p-3 rounded bg-destructive/10 text-destructive text-sm">{error}</div>
            )}

            <div className="flex gap-2 mb-6">
                <button
                    onClick={() => setActiveTab('pending')}
                    className={cn(
                        "px-4 py-2 text-xs font-bold uppercase tracking-wider rounded transition-colors",
                        activeTab === 'pending'
                            ? "bg-white text-black"
                            : "bg-white/10 text-white/60 hover:bg-white/20"
                    )}
                >
                    Pendientes
                </button>
                <button
                    onClick={() => setActiveTab('history')}
                    className={cn(
                        "px-4 py-2 text-xs font-bold uppercase tracking-wider rounded transition-colors",
                        activeTab === 'history'
                            ? "bg-white text-black"
                            : "bg-white/10 text-white/60 hover:bg-white/20"
                    )}
                >
                    Historial
                </button>
            </div>

            {activeTab === 'pending' && (
                <div className="space-y-6">
                    {selectedAttendance ? (
                        <Card className="bg-[#0a0a0a] border-hairline-dark">
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="text-sm font-semibold uppercase tracking-wide">
                                            {selectedAttendance.class_name}
                                        </CardTitle>
                                        <p className="text-xs text-white/60 mt-1">
                                            {selectedAttendance.attended_at}
                                        </p>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-white/60 hover:text-white"
                                        onClick={() => {
                                            setSelectedAttendance(null);
                                            setSelectedRating(null);
                                            setComment('');
                                        }}
                                    >
                                        Volver
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {submitted ? (
                                    <div className="text-center py-8 space-y-3">
                                        <CheckCircle className="h-12 w-12 text-green-400 mx-auto" />
                                        <p className="text-lg font-bold text-white uppercase tracking-wide">
                                            ¡Gracias por tu feedback!
                                        </p>
                                        <p className="text-sm text-white/60">
                                            Tu opinión nos ayuda a mejorar.
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <p className="text-sm font-medium text-white/80 mb-3 text-center uppercase tracking-wider">
                                                ¿Cómo fue tu experiencia?
                                            </p>
                                            <div className="flex justify-center gap-3 flex-wrap">
                                                {FACES.map(face => (
                                                    <button
                                                        key={face.rating}
                                                        onClick={() => setSelectedRating(face.rating)}
                                                        className={cn(
                                                            "flex flex-col items-center gap-1 p-3 rounded-lg transition-all min-w-[72px]",
                                                            selectedRating === face.rating
                                                                ? "bg-white/20 ring-1 ring-white scale-105"
                                                                : "bg-white/5 hover:bg-white/10"
                                                        )}
                                                    >
                                                        <span className="text-3xl leading-none">{face.emoji}</span>
                                                        <span className="text-[10px] font-medium text-white/70 uppercase tracking-wider text-center leading-tight">
                                                            {face.label}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-xs font-medium text-white/60 uppercase tracking-wider mb-2 block">
                                                Comentario o sugerencia (opcional)
                                            </label>
                                            <Textarea
                                                value={comment}
                                                onChange={e => setComment(e.target.value)}
                                                placeholder="Contanos qué te gustó o qué podríamos mejorar..."
                                                className="bg-black border-hairline-dark text-white placeholder:text-white/30 min-h-[100px]"
                                                maxLength={1000}
                                            />
                                        </div>

                                        <Button
                                            onClick={handleSubmit}
                                            disabled={!selectedRating || submitting}
                                            className="w-full btn-ghost"
                                        >
                                            {submitting ? 'Enviando...' : 'Enviar calificación'}
                                        </Button>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    ) : (
                        <>
                            {attendances.length === 0 ? (
                                <Card className="bg-[#0a0a0a] border-hairline-dark">
                                    <CardContent className="py-12 text-center space-y-3">
                                        <ThumbsUp className="h-12 w-12 text-white/40 mx-auto" />
                                        <p className="text-white/60 text-sm">No tenés clases pendientes por calificar.</p>
                                        <p className="text-white/40 text-xs">¡Gracias por participar!</p>
                                    </CardContent>
                                </Card>
                            ) : (
                                <div className="grid gap-4">
                                    {attendances.map(a => (
                                        <Card
                                            key={a.attendance_id}
                                            className="bg-[#0a0a0a] border-hairline-dark cursor-pointer hover:border-white/30 transition-colors"
                                            onClick={() => setSelectedAttendance(a)}
                                        >
                                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                                                        <CalendarDays className="h-4 w-4" />
                                                    </div>
                                                    <div>
                                                        <CardTitle className="text-sm font-semibold uppercase tracking-wide">
                                                            {a.class_name}
                                                        </CardTitle>
                                                        <p className="text-xs text-white/60">{a.attended_at}</p>
                                                    </div>
                                                </div>
                                                <Badge variant="outline" className="text-xs border-white/20 text-white/60">
                                                    Calificar
                                                </Badge>
                                            </CardHeader>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {activeTab === 'history' && (
                <div className="space-y-4">
                    {ratings.length === 0 ? (
                        <Card className="bg-[#0a0a0a] border-hairline-dark">
                            <CardContent className="py-12 text-center">
                                <Star className="h-12 w-12 text-white/40 mx-auto mb-4" />
                                <p className="text-white/60 text-sm">Aún no calificaste ninguna clase.</p>
                            </CardContent>
                        </Card>
                    ) : (
                        ratings.map(r => {
                            const face = FACES.find(f => f.rating === r.rating);
                            return (
                                <Card key={r.id} className="bg-[#0a0a0a] border-hairline-dark">
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center text-lg">
                                                {face?.emoji}
                                            </div>
                                            <div>
                                                <CardTitle className="text-sm font-semibold uppercase tracking-wide">
                                                    {r.gym_class?.name || 'Clase'}
                                                </CardTitle>
                                                <p className="text-xs text-white/60">{formatDateTimeShort(r.created_at)}</p>
                                            </div>
                                        </div>
                                        <Badge className="bg-white/10 text-white border-none text-xs">
                                            {face?.label}
                                        </Badge>
                                    </CardHeader>
                                    {r.comment && (
                                        <CardContent className="pt-0">
                                            <div className="flex items-start gap-2 text-sm text-white/70">
                                                <MessageSquare className="h-4 w-4 mt-0.5 shrink-0 text-white/40" />
                                                <p>{r.comment}</p>
                                            </div>
                                        </CardContent>
                                    )}
                                </Card>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
}
